import React, { useCallback, useState } from 'react';
import {
  Check, ChevronDown, ChevronRight, Copy, Hash, Loader2, Pencil, RotateCcw,
} from 'lucide-react';
import { ivrQa } from '../api';

// Shown while the real list is still loading, or if the request fails. Kept
// short and non-specific on purpose: the authoritative list comes from the API,
// which reads it from the same module the prompt itself comes from.
const FALLBACK_SECTIONS = [
  { title: 'The route to take on this call' },
  { title: 'Paths already taken' },
  { title: 'Details to use if asked' },
];

const DtmfTab = ({ agent, formData, setFormData }) => {
  const dtmfEnabled = formData.dtmf_enabled ?? false;
  const ivrDetection = formData.dtmf_ivr_detection ?? false;
  const qaMode = formData.ivr_qa_mode ?? false;

  // The built-in prompt is fetched, not held here: the copy that reaches a
  // call lives in the agent, and a constant in this file would drift from it.
  const [promptMeta, setPromptMeta] = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState(null);
  const [showBuiltIn, setShowBuiltIn] = useState(false);
  const [copied, setCopied] = useState(false);

  const builtIn = promptMeta?.prompt || '';
  // An empty override means the built-in prompt is in force. That is the same
  // condition the agent applies, so the badge cannot disagree with the call.
  const usingOverride = !!(formData.ivr_qa_prompt_override || '').trim();

  // Fetched on first use rather than on mount: most agents are not QA agents
  // and this panel should not cost a request to open the form.
  const loadPrompt = useCallback(() => {
    if (promptMeta || promptLoading) return Promise.resolve(promptMeta);
    setPromptLoading(true);
    setPromptError(null);
    return ivrQa.defaultPrompt()
      .then((res) => { setPromptMeta(res.data); return res.data; })
      .catch((err) => {
        setPromptError(err.response?.data?.detail || 'Could not load the built-in prompt');
        return null;
      })
      .finally(() => setPromptLoading(false));
  }, [promptMeta, promptLoading]);

  const handleToggleBuiltIn = (e) => {
    e.preventDefault();
    setShowBuiltIn((v) => !v);
    loadPrompt();
  };

  // Tuning starts from the real thing. Handing over an empty box is what made
  // the override unusable: nobody could tune wording they could not read.
  const handleTunePrompt = (e) => {
    e.preventDefault();
    loadPrompt().then((meta) => {
      const text = meta?.prompt || builtIn;
      if (!text) return;
      setFormData({ ...formData, ivr_qa_prompt_override: text });
      setShowBuiltIn(false);
    });
  };

  const handleResetPrompt = (e) => {
    e.preventDefault();
    setFormData({ ...formData, ivr_qa_prompt_override: '' });
  };

  const handleCopyPrompt = (e) => {
    e.preventDefault();
    if (!builtIn) return;
    try {
      navigator.clipboard.writeText(builtIn);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is blocked in some embedded contexts. The prompt is on
      // screen either way, so this is not worth an error message.
    }
  };

  const handleToggleDtmf = (e) => {
    e.preventDefault();
    const newEnabled = !dtmfEnabled;
    setFormData({
      ...formData,
      dtmf_enabled: newEnabled,
      // IVR navigation is a sub-setting, so it goes off with the master toggle.
      // QA mode sits under navigation, so it goes off with it too.
      ...(newEnabled ? {} : { dtmf_ivr_detection: false, ivr_qa_mode: false })
    });
  };

  const handleToggleIvr = (e) => {
    e.preventDefault();
    const newIvr = !ivrDetection;
    setFormData({
      ...formData,
      dtmf_ivr_detection: newIvr,
      // QA mode requires navigation, so it cannot outlive it
      ...(newIvr ? {} : { ivr_qa_mode: false })
    });
  };

  const handleToggleQaMode = (e) => {
    e.preventDefault();
    setFormData({
      ...formData,
      ivr_qa_mode: !qaMode
    });
  };

  // Numeric caps are typed, so an empty box must not become NaN and reach the
  // API. Fall back to the column default and clamp to something sane.
  const handleNumberChange = (field, fallback, min, max) => (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setFormData({ ...formData, [field]: fallback });
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    setFormData({ ...formData, [field]: Math.min(Math.max(parsed, min), max) });
  };

  return (
    <div className="flex-1 overflow-y-auto min-h-0 p-6"
      style={{
        scrollBehavior: 'smooth',
        maxHeight: 'calc(100vh - 280px)'
      }}
    >
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
              <Hash className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground">Touch-Tone Keypad (DTMF)</h3>
              <p className="text-sm text-muted-foreground">Let the agent read keypresses and press keys of its own</p>
            </div>
          </div>

          {/* Master toggle */}
          <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
            <div className="min-w-0 pr-4">
              <label className="text-sm font-medium text-foreground">Enable DTMF</label>
              <p className="text-xs text-muted-foreground mt-1">
                When enabled, keys the caller presses are passed to the agent as caller
                input, and the agent gains a tool that presses keys back down the line.
                Off by default, and with it off the call behaves exactly as it does today.
              </p>
            </div>
            <button
              type="button"
              onClick={handleToggleDtmf}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                dtmfEnabled ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 flex-shrink-0 transform rounded-full bg-white transition-transform ${
                  dtmfEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {dtmfEnabled && (
            <div className="mt-4 space-y-4">
              {/* IVR navigation sub-toggle */}
              <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
                <div className="min-w-0 pr-4">
                  <label className="text-sm font-medium text-foreground">IVR Navigation</label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Detect when an automated phone menu answers and let the agent work
                    through it, choosing options with the keypad until it reaches a person
                    or completes the task. Useful on outbound calls that land in a phone
                    tree. Leave this off for normal customer conversations.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleIvr}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    ivrDetection ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 flex-shrink-0 transform rounded-full bg-white transition-transform ${
                      ivrDetection ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* How it works */}
              <div className="p-4 bg-muted/20 rounded-lg border border-border/50">
                <label className="text-sm font-medium text-foreground">How it works</label>
                <ul className="text-xs text-muted-foreground mt-2 space-y-1.5 list-disc list-inside">
                  <li>Keys the caller presses arrive as tones on the call, not as speech, so the agent only sees them once this is on.</li>
                  <li>Digits pressed in quick succession are grouped into one entry, so an account number reaches the agent whole.</li>
                  <li>Keypresses show up in the call transcript as <span className="font-medium text-foreground">[keypad: 1234]</span>.</li>
                  <li>The agent presses keys with a send_dtmf tool. Speaking a digit out loud does not press it.</li>
                  <li>Valid keys are 0 through 9, plus <span className="font-medium text-foreground">*</span> and <span className="font-medium text-foreground">#</span>.</li>
                  <li>Add your own guidance to the system prompt, such as which menu option to choose, if the agent needs it.</li>
                </ul>
              </div>

              {ivrDetection && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    IVR navigation adds menu detection to every call this agent takes. On
                    outbound calls with Voicemail Drop enabled, answering machine detection
                    runs first and hands off to IVR navigation only when the call reaches
                    an automated menu rather than a recording.
                  </p>
                </div>
              )}

              {/* IVR QA mode: turns a navigating agent into an auditing agent */}
              {ivrDetection && (
                <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
                  <div className="min-w-0 pr-4">
                    <label className="text-sm font-medium text-foreground">IVR Quality Assurance</label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Audit the phone tree instead of just walking it. The agent times every
                      menu, takes a different route on each call, detects loops, records how
                      long it took to reach a person, and reports all of it. Use this to find
                      out what is actually inside an IVR. Leave it off for agents that need to
                      get through a menu to do a job on the other side.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleQaMode}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                      qaMode ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 flex-shrink-0 transform rounded-full bg-white transition-transform ${
                        qaMode ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}

              {ivrDetection && qaMode && (
                <div className="space-y-4">
                  {/* What it measures */}
                  <div className="p-4 bg-muted/20 rounded-lg border border-border/50">
                    <label className="text-sm font-medium text-foreground">What gets recorded</label>
                    <ul className="text-xs text-muted-foreground mt-2 space-y-1.5 list-disc list-inside">
                      <li>Time to reach a human, measured from the start of the IVR conversation. Empty when no person was ever reached, which is itself a finding.</li>
                      <li>Per menu: what it said, the options it announced, how long it spoke, and what we pressed.</li>
                      <li>The route taken, so the next call can be steered down a branch nobody has walked yet.</li>
                      <li>Loops, dead branches, and announcements that have gone stale.</li>
                      <li>All timings come from call events, never from the agent's own estimate.</li>
                    </ul>
                  </div>

                  {/* Safety caps */}
                  <div className="p-4 bg-muted/20 rounded-lg border border-border/50 space-y-4">
                    <div>
                      <label className="text-sm font-medium text-foreground">Safety limits</label>
                      <p className="text-xs text-muted-foreground mt-1">
                        A phone tree has no natural end. These are the outer bounds, so a call
                        that is getting nowhere stops burning minutes.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-foreground">Max menus per call</label>
                        <input
                          type="number"
                          min="3"
                          max="100"
                          value={formData.ivr_qa_max_turns ?? 25}
                          onChange={handleNumberChange('ivr_qa_max_turns', 25, 3, 100)}
                          className="mt-1 w-full px-3 py-2 text-sm bg-background border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Default 25</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-foreground">Max call length (seconds)</label>
                        <input
                          type="number"
                          min="30"
                          max="1800"
                          value={formData.ivr_qa_max_duration_s ?? 600}
                          onChange={handleNumberChange('ivr_qa_max_duration_s', 600, 30, 1800)}
                          className="mt-1 w-full px-3 py-2 text-sm bg-background border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Default 600 (10 minutes). A call still reaching new menus can run up to twice this.</p>
                      </div>
                    </div>
                  </div>

                  {/* Goal */}
                  <div className="p-4 bg-muted/20 rounded-lg border border-border/50">
                    <label className="text-sm font-medium text-foreground">What this agent is auditing for</label>
                    <select
                      value={formData.ivr_qa_goal || 'reach_operator'}
                      onChange={(e) => setFormData({ ...formData, ivr_qa_goal: e.target.value })}
                      className="mt-2 w-full px-3 py-2 text-sm bg-background border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="reach_operator">Reach a human operator</option>
                      <option value="explore">Map the tree (explore every branch)</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-2">
                      Reaching an operator ends the call as soon as a person answers and logs how
                      long that took. Mapping keeps going to cover more of the tree per call.
                    </p>
                  </div>

                  {/* The prompt itself
                    *
                    * Prompt tuning is most of the remaining work on this
                    * feature, and it was being done blind: the override
                    * replaces the built-in block and there was no way to read
                    * the block being replaced. So the built-in prompt is shown
                    * here, and "Tune it" starts an override from that exact
                    * text rather than from an empty box.
                    *
                    * The prompt is fetched rather than held as a constant here
                    * because the version that reaches a call lives in the
                    * agent. A copy in this file would drift, and an operator
                    * would tune a prompt no call has ever run. */}
                  <div className="p-4 bg-muted/20 rounded-lg border border-border/50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <label className="text-sm font-medium text-foreground">
                          The QA prompt
                        </label>
                        <p className="text-xs text-muted-foreground mt-1">
                          {usingOverride
                            ? 'This agent runs your edited prompt. The built-in one is still there to compare against or go back to.'
                            : 'This agent runs the built-in prompt below. Tune it if a real call goes wrong in a way the wording can fix.'}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded flex-shrink-0 ${
                        usingOverride
                          ? 'bg-primary/15 text-primary'
                          : 'bg-muted/50 text-muted-foreground'
                      }`}>
                        {usingOverride ? 'Edited' : 'Built-in'}
                      </span>
                    </div>

                    {/* What is appended no matter what. Worth showing next to
                        the editor: a hand-written route in an override would be
                        contradicted by the real one a few lines later. */}
                    <div className="mt-3 p-3 bg-background/60 border border-border/50 rounded-lg">
                      <p className="text-xs font-medium text-foreground">
                        Added automatically on every call, whether you edit the prompt or not
                      </p>
                      <ul className="mt-2 space-y-1.5">
                        {(promptMeta?.appended_sections || FALLBACK_SECTIONS).map((s) => (
                          <li key={s.title} className="text-xs text-muted-foreground">
                            <span className="text-foreground">{s.title}</span>
                            {s.when ? <span className="opacity-70"> · {s.when}</span> : null}
                            {s.detail ? <div className="opacity-80">{s.detail}</div> : null}
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs text-muted-foreground mt-2">
                        Do not write these into the prompt yourself. The route is computed from
                        what earlier calls found, and a hand-written one would be contradicted a
                        few lines later.
                      </p>
                    </div>

                    {/* Read the built-in prompt. Collapsed by default: it is
                        five thousand characters and this panel is not the place
                        for a wall of text nobody asked to see. */}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={handleToggleBuiltIn}
                        className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                      >
                        {showBuiltIn ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        {showBuiltIn ? 'Hide the built-in prompt' : 'Read the built-in prompt'}
                      </button>
                      {!usingOverride && (
                        <button
                          type="button"
                          onClick={handleTunePrompt}
                          disabled={promptLoading}
                          className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg bg-gradient-button text-primary-foreground shadow-md hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                          {promptLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pencil className="w-3.5 h-3.5" />}
                          Tune it
                        </button>
                      )}
                      {usingOverride && (
                        <button
                          type="button"
                          onClick={handleResetPrompt}
                          title="Discard your edits and go back to the built-in prompt"
                          className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg border border-border/60 text-muted-foreground hover:text-destructive hover:border-destructive/40 hover:bg-destructive/5 transition-colors"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          Back to built-in
                        </button>
                      )}
                      {builtIn && (
                        <button
                          type="button"
                          onClick={handleCopyPrompt}
                          className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                        >
                          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copied ? 'Copied' : 'Copy'}
                        </button>
                      )}
                    </div>

                    {promptError && (
                      <p className="text-xs text-destructive mt-2">{promptError}</p>
                    )}

                    {showBuiltIn && (
                      <div className="mt-3">
                        {promptLoading && !builtIn ? (
                          <p className="text-xs text-muted-foreground">Loading the prompt...</p>
                        ) : (
                          <pre className="max-h-80 overflow-auto p-3 text-xs leading-relaxed bg-background border border-border/60 rounded-lg text-foreground whitespace-pre-wrap font-mono">
                            {builtIn}
                          </pre>
                        )}
                      </div>
                    )}

                    {/* The editor. Shown once there is an override to edit, so
                        the default view is not a blank box implying the prompt
                        is empty - which is how it read before, and why nobody
                        could tell what they were replacing. */}
                    {usingOverride && (
                      <div className="mt-4">
                        <label className="text-xs font-medium text-foreground">
                          Your prompt (replaces the built-in one)
                        </label>
                        <textarea
                          rows="18"
                          value={formData.ivr_qa_prompt_override || ''}
                          onChange={(e) => setFormData({ ...formData, ivr_qa_prompt_override: e.target.value })}
                          className="mt-2 w-full px-3 py-2 text-xs leading-relaxed bg-background border border-border/60 rounded-lg text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                        <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
                          <p className="text-xs text-muted-foreground">
                            Takes effect on the next call. No deploy needed.
                          </p>
                          <p className="text-xs text-muted-foreground tabular-nums">
                            {(formData.ivr_qa_prompt_override || '').length} characters
                            {builtIn ? ` · built-in is ${builtIn.length}` : ''}
                          </p>
                        </div>
                        {builtIn && (formData.ivr_qa_prompt_override || '').trim() === builtIn.trim() && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Identical to the built-in prompt so far. Saving it as-is changes
                            nothing except that later fixes to the built-in one will not reach
                            this agent.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      QA mode is for auditing lines you are authorized to audit. Space the calls
                      out and keep concurrency low: the goal is to measure a phone tree, not to
                      put load on it.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DtmfTab;
